import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaBook, FaCheckCircle } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const LessonManagement = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [formData, setFormData] = useState({ lessonNumber: '', title: '', description: '', price: 0 });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchLessons = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/lessons`, { headers });
      const data = await res.json();
      if (data.success) setLessons(data.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLessons(); }, []);

  const openCreate = () => {
    setEditingLesson(null);
    setFormData({ lessonNumber: '', title: '', description: '', price: 0 });
    setShowForm(true);
  };

  const openEdit = (lesson) => {
    setEditingLesson(lesson);
    setFormData({ 
      lessonNumber: lesson.lessonNumber || '', 
      title: lesson.title, 
      description: lesson.description,
      price: lesson.price || 0
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = editingLesson
        ? `${API_BASE}/lessons/${editingLesson._id}`
        : `${API_BASE}/lessons`;
      const method = editingLesson ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(formData) });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: editingLesson ? 'Lesson updated!' : 'Lesson created!' });
        setShowForm(false);
        fetchLessons();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Server error' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lesson? Videos inside it will not be deleted.')) return;
    try {
      const res = await fetch(`${API_BASE}/lessons/${id}`, { method: 'DELETE', headers });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Lesson deleted!' });
        fetchLessons();
      }
    } catch (e) {
      setMessage({ type: 'error', text: 'Error deleting lesson' });
    }
    setTimeout(() => setMessage(null), 3000);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FaBook className="text-orange-500 text-2xl" />
          <h2 className="text-xl font-bold text-gray-900">Manage Lessons</h2>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
        >
          <FaPlus /> New Lesson
        </button>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm font-medium flex items-center gap-2 ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message.type === 'success' && <FaCheckCircle />} {message.text}
        </div>
      )}

      {showForm && (
        <div className="mb-6 p-5 bg-orange-50 border border-orange-200 rounded-xl">
          <h3 className="font-semibold text-gray-800 mb-4">{editingLesson ? 'Edit Lesson' : 'Create New Lesson'}</h3>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lesson No. *</label>
                <input
                  type="number"
                  required
                  min="1"
                  value={formData.lessonNumber}
                  onChange={e => setFormData(p => ({ ...p, lessonNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g. 1"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g., Introduction to Tabla"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹) <span className="text-gray-400 text-xs">(0 for free)</span></label>
                <input
                  type="number"
                  min="0"
                  value={formData.price}
                  onChange={e => setFormData(p => ({ ...p, price: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="e.g. 499"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                required
                rows={3}
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Describe what students will learn in this lesson"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingLesson ? 'Update Lesson' : 'Create Lesson'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-5 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading lessons...</div>
      ) : lessons.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <FaBook className="text-5xl mx-auto mb-3 opacity-30" />
          <p>No lessons yet. Create your first lesson above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {lessons.map((lesson, index) => (
            <div key={lesson._id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200 hover:border-orange-300 transition">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center text-orange-700 font-bold text-sm flex-shrink-0">
                  L{lesson.lessonNumber ?? index + 1}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">
                      Lesson {lesson.lessonNumber ?? index + 1}: {lesson.title}
                    </p>
                    {lesson.price > 0 ? (
                      <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-bold uppercase">
                        ₹{lesson.price}
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-bold uppercase">
                        Free
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-1">{lesson.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEdit(lesson)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                  title="Edit"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDelete(lesson._id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                  title="Delete"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LessonManagement;
